import { useT } from '@hana/i18n';
import '../i18n';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  IconArrowRight, IconBook, IconBox, IconCalendar, IconKey as IconKeyMark, IconList, IconChecklist, IconClock, IconExternalLink, IconFileText, IconGridDots, IconLock, IconMail, IconMessageReport, IconMoon,
  IconNote, IconPaperclip, IconPencil, IconPlayerPlay, IconPlus, IconShip, IconStar, IconSun, IconTruck, IconX, IconMinus, IconSquare,
} from '@tabler/icons-react';
import { AppIcon, Button, Modal, useTheme, useToast, type ScreenState } from '@hana/ui';
import { AppsMenu, Feedback } from '@hana/shell';
import { clearAskHandoff, peekAskHandoff, useAsk, useAskAvailable } from '@hana/ask-hana';
import { Owl } from './Owl';
import {
  CONFIRM, CONV, HIST, ROLES, SAMPLE_FILES, SAVED, answer,
  type AskAct, type CiteKey, type ConvKey, type IconKey, type Msg, type RoleKey,
} from './data';
import s from './askhana.module.css';

// Mint-AskHana (desktop) + Mint-AskHana-Phone: trợ lý toàn trang, rail riêng (không dùng menu ERP). Dữ liệu mẫu trong bộ nhớ, không nối mô hình thật.
// Chỉ đọc và gợi ý: không trả số tiền, không tự thực thi; thao tác ghi chỉ mở hộp xác nhận rồi sang HANA Ezy. Chưa làm: popover Chuyển app và bảng Góp ý
// (bộ dùng chung của shell — A lắp), bảng Lịch sử trên iPad.

const ICON: Record<IconKey, ReactNode> = {
  job: <IconBox />, file: <IconFileText />, ship: <IconShip />, mail: <IconMail />, task: <IconChecklist />, clock: <IconClock />, note: <IconNote />, clip: <IconPaperclip />, truck: <IconTruck />,
};
const CITE_ICON: Record<CiteKey, ReactNode> = {
  job: <IconList size={14} stroke={1.9} aria-hidden="true" />, sop: <IconBook size={14} stroke={1.9} aria-hidden="true" />, money: <IconKeyMark size={14} stroke={1.9} aria-hidden="true" />,
  sched: <IconCalendar size={14} stroke={1.9} aria-hidden="true" />, box: <IconMail size={14} stroke={1.9} aria-hidden="true" />, booking: <IconChecklist size={14} stroke={1.9} aria-hidden="true" />,
  bl: <IconFileText size={14} stroke={1.9} aria-hidden="true" />, tasks: <IconChecklist size={14} stroke={1.9} aria-hidden="true" />, tms: <IconTruck size={14} stroke={1.9} aria-hidden="true" />,
  customers: <IconBox size={14} stroke={1.9} aria-hidden="true" />, jobDetail: <IconBox size={14} stroke={1.9} aria-hidden="true" />,
};
const ico = (k: IconKey, size: number) => <span className={s.ic} aria-hidden="true" style={{ width: size, height: size }}>{ICON[k]}</span>;

function readQuery(): { state: ScreenState; conv: ConvKey | null; instant: boolean } {
  const q = new URLSearchParams(location.search);
  const v = q.get('state');
  const c = q.get('conv') as ConvKey | null;
  return {
    state: v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : 'ready',
    conv: c && c in CONV ? c : null,
    instant: q.has('fx'),
  };
}

function useCompact(): boolean {
  const q = '(max-width: 699px)';
  const [c, setC] = useState(() => typeof matchMedia === 'function' && matchMedia(q).matches);
  useEffect(() => {
    const m = matchMedia(q);
    const f = () => setC(m.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, []);
  return c;
}

function JobsCard({ m, compact }: { m: Extract<Msg, { kind: 'jobs' }>; compact: boolean }) {
  return (
    <div role="table" aria-label="Job sắp cut-off" className={s.card}>
      {m.rows.map((r) => (
        <a key={r.id} role="row" href="/ezy/jobs" target="_blank" rel="noopener" title={r.sub} className={s.jrow}>
          <b role="cell" className={s.jid}>{r.id}</b>
          <span role="cell" className={s.jroute}>{compact ? r.route : `${r.route} · ${r.sub}`}</span>
          <span role="cell" aria-label={`Cut-off SI còn ${r.left}`} className={s.jbar}>
            <span className={s[r.tone]}>{r.left}</span>
            <span className={s.track}><span className={`${s.fill} ${s[`bg_${r.tone}`]}`} style={{ width: `${r.pct}%` }} /></span>
          </span>
          <span role="cell" title={r.st} className={`${s.dot} ${r.st === 'BK Confirmed' ? s.dBlue : s.dAmber}`} />
          <span aria-hidden="true" className={s.ext}><IconExternalLink size={15} stroke={1.9} /></span>
        </a>
      ))}
    </div>
  );
}

function DocCard({ m }: { m: Extract<Msg, { kind: 'doc' }> }) {
  return (
    <div className={s.card}>
      <div className={s.docHead}>
        <span className={s.pdf}>PDF</span>
        <b className={s.docName}>{m.doc[0]}</b>
        <span className={s.docSub}>{m.doc[1]}</span>
      </div>
      {m.fields.map((f) => (
        <div key={f.k} title={f.ok ? 'Khớp job' : f.why} className={s.frow}>
          <span className={s.fk}>{f.k}</span>
          <span className={s.fv}>{f.v}</span>
          <span aria-label={f.ok ? 'Khớp job' : (f.why ?? 'Lệch job')} className={`${s.fmark} ${f.ok ? s.fOk : s.fWarn}`}>{f.ok ? '✓' : '!'}</span>
        </div>
      ))}
    </div>
  );
}

function Bubble({ m, i, lastAi, justDone, onAct, compact }: { m: Msg; i: number; lastAi: number; justDone: boolean; onAct: (a: AskAct) => void; compact: boolean }) {
  const t = useT();
  if ('me' in m && m.me) {
    return (
      <div className={s.me}>
        {m.files && m.files.length > 0 && (
          <div className={s.meFiles}>
            {m.files.map((f) => <span key={f} className={s.fchip}><IconFileText size={15} stroke={1.9} aria-hidden="true" />{f}</span>)}
          </div>
        )}
        <div className={s.meText}>{m.text}</div>
      </div>
    );
  }
  const a = m as Exclude<Msg, { me: true }>;
  const acts = 'acts' in a ? a.acts : [];
  return (
    <div className={s.ai}>
      <span className={s.aiAva}><Owl kind={justDone && i === lastAi ? 'done' : 'idle'} size={justDone && i === lastAi ? 28 : 26} /></span>
      <div className={s.aiBody}>
        <div className={s.aiText}>{a.text}</div>
        {a.kind === 'jobs' && <JobsCard m={a} compact={compact} />}
        {a.kind === 'doc' && <DocCard m={a} />}
        {a.kind === 'lock' && <div className={s.lockNote}><IconLock size={15} stroke={2} aria-hidden="true" /><b>{t('box.noMoney')}</b></div>}
        {acts.length > 0 && (
          <div className={s.acts}>
            {acts.map((x) => (
              <button key={x.id} type="button" className={`${s.act} ${x.primary ? s.actPri : ''}`} onClick={() => onAct(x)}>
                {x.id === 'remind' ? <IconMail size={16} stroke={1.9} aria-hidden="true" /> : x.id === 'note' ? <IconNote size={16} stroke={1.9} aria-hidden="true" /> : <IconExternalLink size={16} stroke={1.9} aria-hidden="true" />}
                {x.k}
              </button>
            ))}
          </div>
        )}
        {a.cites.length > 0 && (
          <div className={s.cites}>
            {a.cites.map((c) => <a key={c.k} href={c.to} target="_blank" rel="noopener" className={s.cite}>{CITE_ICON[c.key]}{c.k} ↗</a>)}
          </div>
        )}
      </div>
    </div>
  );
}

/** Rail riêng của Ask HANA nằm ngoài main của shell (đúng board: rail đứng ngoài khung chat), gắn đầu body để thứ tự Tab: rail trước. */
function RailPortal({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = document.createElement('div');
    document.body.prepend(el);
    setHost(el);
    return () => { el.remove(); };
  }, []);
  return host ? createPortal(children, host) : null;
}

export function AskHanaPage() {
  const t = useT();
  const q = useMemo(readQuery, []);
  const compact = useCompact();
  const { theme, toggle } = useTheme();
  const toast = useToast();
  const [apps, setApps] = useState<'rail' | 'float' | null>(null);
  const [fb, setFb] = useState(false);
  const overlays = (
    <>
      {apps && <AppsMenu current="ask" at={apps} onClose={() => setApps(null)} />}
      <Feedback open={fb} onOpenChange={setFb} zone="Ask HANA" screen="Trợ lý toàn trang" />
    </>
  );

  // Hội thoại chuyển từ AskDock (nút "Mở toàn trang"): hiện nguyên messages, không gọi lại mô hình.
  const [handed] = useState(peekAskHandoff);
  useEffect(() => { clearAskHandoff(); }, []);
  const [conv, setConv] = useState<ConvKey>(handed ? 'fresh' : q.state === 'empty' ? 'new' : (q.conv ?? (compact ? 'new' : 'cutoff')));
  const [extra, setExtra] = useState<Msg[]>(handed ?? []);
  const [think, setThink] = useState(false);
  const [done, setDone] = useState(false);
  const [att, setAtt] = useState<string[]>([]);
  const [aq, setAq] = useState('');
  const [role, setRole] = useState<RoleKey>('sale');
  const [panel, setPanel] = useState<'hist' | 'saved'>('hist');
  const [sheet, setSheet] = useState(false);
  const [cf, setCf] = useState<'remind' | 'note' | null>(null);
  // Điện thoại: mở một hội thoại = màn đọc riêng (board khung 2: đầu trang chỉ có cú + tên, không khung soạn); nút Back của máy quay về "Hỏi mới".
  const [read, setRead] = useState(false);
  useEffect(() => {
    const back = () => setRead(false);
    addEventListener('popstate', back);
    return () => removeEventListener('popstate', back);
  }, []);
  const enterRead = () => { if (!read) history.pushState({ ask: 1 }, ''); setRead(true); };
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const finish = (m: Msg) => {
    setExtra((e) => [...e, m]);
    setThink(false);
    setDone(true);
    timers.current.push(window.setTimeout(() => setDone(false), 1600));
  };
  // Thật: gọi worker `ask` qua useAsk. Mẫu: `?fx=1` (cổng so ảnh) hoặc Hub chưa lắp AskProvider.
  const live = useAskAvailable() && !q.instant;
  const ai = useAsk(finish);
  const aiErr = ai.status === 'error';
  useEffect(() => { if (aiErr) setThink(false); }, [aiErr]);

  const { state } = q;
  const empty = state === 'empty';
  const noperm = state === 'noperm';
  const load = state === 'loading';
  const isNew = conv === 'new' && !load;
  const all = [...(conv === 'new' ? [] : CONV[conv][1]), ...extra];
  const lastAi = all.map((m) => 'ai' in m && !!m.ai).lastIndexOf(true);

  const ask = (text: string) => {
    const t = text.trim();
    if ((!t && att.length === 0) || noperm) return;
    const files = att.slice();
    setExtra((e) => [...e, { me: true, text: t || 'Đọc giúp chứng từ đính kèm', files }]);
    setAq('');
    setAtt([]);
    setThink(true);
    setDone(false);
    if (conv === 'new') setConv('fresh');
    if (compact) enterRead();
    if (live) {
      ai.ask([...(conv === 'new' ? [] : CONV[conv][1]), ...extra], t || 'Đọc giúp chứng từ đính kèm');
      return;
    }
    const reply = () => finish(answer(t, files));
    if (q.instant) reply();
    else timers.current.push(window.setTimeout(reply, 900));
  };
  const openConv = (k: ConvKey) => { setConv(k); setExtra([]); setThink(false); setAtt([]); setSheet(false); if (compact) enterRead(); };
  const newConv = () => { setConv('new'); setExtra([]); setThink(false); setAtt([]); setSheet(false); setRead(false); };
  const showPanel = (p: 'hist' | 'saved') => { setPanel(p); if (compact) setSheet(true); if (p === 'hist' && conv === 'new' && !empty) { setConv('cutoff'); setExtra([]); } };
  const attach = () => {
    if (noperm) return;
    const next = SAMPLE_FILES.find((f) => !att.includes(f));
    if (next) setAtt([...att, next]);
    else toast({ msg: 'Tối đa 2 tệp mỗi câu hỏi' });
  };
  const onAct = (a: AskAct) => { if (a.id === 'open') toast({ msg: 'Mở B/L trong Ezy ↗' }); else setCf(a.id); };

  const sideTitle = panel === 'saved' ? 'Lối tắt đã lưu' : 'Lịch sử hội thoại';
  const hist = empty ? [] : HIST;
  const histOn = conv !== 'new' && panel === 'hist';
  const savedOn = conv !== 'new' && panel === 'saved';

  const side = panel === 'saved' ? (
    SAVED.map(([k, d, qq]) => (
      <button key={k} type="button" className={s.hrow} title={qq} onClick={() => { setSheet(false); ask(qq); }}>{ico(d, compact ? 18 : 16)}<span>{k}</span></button>
    ))
  ) : hist.length === 0 ? (
    <div className={s.sideNone}>{t('box.noConv')}</div>
  ) : (
    hist.map(([cap, rows]) => (
      <div key={cap} className={s.grp}>
        <div className={s.cap}>{cap}</div>
        {rows.map(([k, d]) => (
          <button key={k} type="button" className={`${s.hrow} ${conv === k ? s.hrowOn : ''}`} aria-current={conv === k ? 'page' : undefined} title={CONV[k][0]} onClick={() => openConv(k)}>
            {ico(d, compact ? 18 : 16)}<span>{CONV[k][0]}</span>
          </button>
        ))}
      </div>
    ))
  );

  const stageBody = (
    <>
      {(state === 'error' || aiErr) && (
        <div role="alert" className={s.errBar}>
          <b>{t('box.askLost')}</b>
          <Button variant="primary" icon={<IconPlayerPlay size={16} aria-hidden="true" />} onClick={() => { if (aiErr) { setThink(true); ai.retry(); } else toast({ msg: 'Đang kết nối lại…' }); }}>{t('box.retry')}</Button>
        </div>
      )}
      {noperm && <div role="status" className={s.permBar}><b>{t('box.askNoPerm')}</b> <span>{t('box.askNoPermHint')}</span></div>}
      {load && [1, 2, 3].map((k) => (
        <div key={k} aria-hidden="true" className={s.sk}>
          <span className={s.skAva} />
          <div className={s.skBody}><div className={s.skLine} /><div className={s.skBox} /></div>
        </div>
      ))}
      {isNew && (
        <div className={s.start}>
          <Owl kind="hero" size={104} />
          <b className={s.hero}>{t('box.askAnything')}</b>
          <div role="tablist" aria-label="Gợi ý theo vai" className={s.roles}>
            {(Object.keys(ROLES) as RoleKey[]).map((k) => (
              <button key={k} type="button" role="tab" aria-selected={role === k} className={`${s.role} ${role === k ? s.roleOn : ''}`} onClick={() => setRole(k)}>{ROLES[k][0]}</button>
            ))}
          </div>
          <div className={s.sugg}>
            {ROLES[role][1].map(([k, d]) => (
              <button key={k} type="button" className={s.sg} onClick={() => { if (/^Đọc /.test(k)) { setAtt([SAMPLE_FILES[0]!]); setAq(k); } else ask(k); }}>
                <span className={s.sgIc}>{ico(d, 16)}</span>{k}
              </button>
            ))}
          </div>
        </div>
      )}
      {!load && all.map((m, i) => <Bubble key={i} m={m} i={i} lastAi={lastAi} justDone={done && !think} onAct={onAct} compact={compact} />)}
      {think && (
        <div aria-live="polite" className={s.thinking}>
          <span className={s.thinkOwl}><Owl kind="think" size={44} /><span className={s.spin} aria-hidden="true" /></span>
          <div className={s.thinkLines}><div className={s.skLine} style={{ width: '70%' }} /><div className={s.skLine} style={{ width: '45%' }} /></div>
        </div>
      )}
    </>
  );
  const stage = <div className={s.stage}>{stageBody}</div>;

  const composer = (
    <div className={s.comp}>
      {att.length > 0 && (
        <div className={s.attRow}>
          {att.map((f) => (
            <span key={f} className={s.attChip}>
              <IconFileText size={15} stroke={1.9} aria-hidden="true" />{f}
              <button type="button" aria-label={`Bỏ ${f}`} className={s.attX} onClick={() => setAtt(att.filter((x) => x !== f))}><IconX size={14} stroke={2} aria-hidden="true" /></button>
            </span>
          ))}
        </div>
      )}
      <div className={s.box} style={{ opacity: noperm ? 0.55 : 1 }}>
        <button type="button" className={s.attach} aria-label="Đính kèm ảnh hoặc PDF" title="Đính kèm ảnh / PDF để đọc chứng từ" onClick={attach}><IconPaperclip size={18} stroke={1.9} aria-hidden="true" /></button>
        <input aria-label="Hỏi Ask HANA" placeholder="Hỏi về job, lịch, chứng từ…" value={aq} disabled={noperm} onChange={(e) => setAq(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') ask(aq); }} />
        <button type="button" className={`${s.send} ${aq.trim() || att.length ? s.sendOn : ''}`} aria-label="Gửi câu hỏi" onClick={() => ask(aq)}><IconArrowRight size={17} stroke={2.1} aria-hidden="true" /></button>
      </div>
      <div className={s.guard} title="Ask HANA chỉ đọc và gợi ý. Thao tác ghi luôn qua nút xác nhận và mở màn tương ứng trong HANA Ezy."><IconLock size={13} stroke={2} aria-hidden="true" />{t('box.askGuard')}</div>
    </div>
  );

  const cfd = cf ? CONFIRM[cf] : null;
  const confirm = (
    <Modal
      open={!!cfd}
      onOpenChange={(o) => { if (!o) setCf(null); }}
      title={cfd?.k ?? ''}
      description="Ask HANA không tự gửi · anh duyệt ở Ezy"
      footer={(
        <>
          <Button onClick={() => setCf(null)}>{t('box.cancel')}</Button>
          <Button variant="primary" onClick={() => { setCf(null); toast({ msg: 'Đã mở HANA Ezy · anh duyệt và gửi ở đó' }); }}>{t('box.openInEzy')}</Button>
        </>
      )}
    >
      {cfd && (
        <div className={s.cfRow}><span>{t('box.openAt')}</span><b>HANA Ezy · {cfd.screen}</b><span>{cfd.n}</span></div>
      )}
    </Modal>
  );

  const railNode = (
    <nav aria-label="Ask HANA" className={s.rail}>
          <div className={s.railOwl} title="Ask HANA"><Owl kind="hero" size={40} /></div>
          <button type="button" className={`${s.rb} ${conv === 'new' ? s.rbOn : ''}`} aria-label="Hỏi mới" aria-pressed={conv === 'new'} title="Hỏi mới" onClick={newConv}><IconPencil size={20} stroke={1.9} aria-hidden="true" />{t('box.askNew')}</button>
          <button type="button" className={`${s.rb} ${histOn ? s.rbOn : ''}`} aria-label="Lịch sử hội thoại" aria-pressed={histOn} title="Lịch sử hội thoại" onClick={() => showPanel('hist')}><IconClock size={20} stroke={1.9} aria-hidden="true" />{t('box.history')}</button>
          <button type="button" className={`${s.rb} ${savedOn ? s.rbOn : ''}`} aria-label="Lối tắt đã lưu" aria-pressed={savedOn} title="Lối tắt đã lưu" onClick={() => showPanel('saved')}><IconStar size={20} stroke={1.9} aria-hidden="true" />{t('box.shortcuts')}</button>
          <span className={s.grow} />
          <button type="button" className={s.rb} aria-label="Chuyển app" title="Chuyển app" aria-haspopup="menu" onClick={() => setApps('rail')}><IconGridDots size={20} stroke={2.4} aria-hidden="true" />{t('box.zone')}</button>
          <button type="button" className={s.rIc} aria-label={theme === 'dark' ? 'Giao diện sáng' : 'Giao diện tối'} title={theme === 'dark' ? 'Giao diện sáng' : 'Giao diện tối'} onClick={toggle}>
            {theme === 'dark' ? <IconSun size={18} stroke={1.9} aria-hidden="true" /> : <IconMoon size={18} stroke={1.9} aria-hidden="true" />}
          </button>
          <button type="button" className={s.rIc} aria-label="Góp ý (Ctrl+Shift+F)" title="Góp ý (Ctrl+Shift+F)" onClick={() => setFb(true)}><IconMessageReport size={18} stroke={1.9} aria-hidden="true" /></button>
          <span className={s.me2} title="DAVID · [SALE-MGR]">DA</span>
        </nav>
  );

  if (compact && read && conv !== 'new') {
    return (
      <div className={`${s.root} ${s.phone}`}>
        <header className={s.pHead}>
          <Owl kind="hero" size={40} />
          <b className={s.pTitle}>{CONV[conv][0]}</b>
        </header>
        <div className={s.scroll}><div className={`${s.stage} ${s.readStage}`}>{stageBody}</div></div>
        {confirm}
        {overlays}
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`${s.root} ${s.phone}`}>
        <header className={s.pHead}>
          <Owl kind="hero" size={40} />
          <b className={s.pTitle}>Ask HANA</b>
          <button type="button" className={s.pBtn} aria-label="Góp ý" title="Góp ý cho Admin IT" onClick={() => setFb(true)}><IconMessageReport size={18} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.pBtn} aria-label="Lịch sử hội thoại" onClick={() => showPanel('hist')}><IconClock size={20} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.pBtn} aria-label="Chuyển app" aria-haspopup="menu" onClick={() => setApps('float')}><IconGridDots size={20} stroke={2.4} aria-hidden="true" /></button>
        </header>
        <div className={s.scroll}>{stage}</div>
        {composer}
        {sheet && (
          <>
            <div className={s.scrim} onClick={() => setSheet(false)} />
            <div role="dialog" aria-modal="true" aria-label={sideTitle} className={s.sheet}>
              <div className={s.grab} aria-hidden="true" />
              <div className={s.sheetHead}>
                <b>{sideTitle}</b>
                <button type="button" className={`${s.pBtn} ${histOn ? s.pBtnOn : ''}`} aria-label="Lịch sử hội thoại" onClick={() => setPanel('hist')}><IconClock size={20} stroke={1.9} aria-hidden="true" /></button>
                <button type="button" className={`${s.pBtn} ${savedOn ? s.pBtnOn : ''}`} aria-label="Lối tắt đã lưu" onClick={() => setPanel('saved')}><IconStar size={20} stroke={1.9} aria-hidden="true" /></button>
                <button type="button" className={s.pBtn} aria-label="Hỏi mới" onClick={newConv}><IconPencil size={20} stroke={1.9} aria-hidden="true" /></button>
              </div>
              {side}
            </div>
          </>
        )}
        {confirm}
        {overlays}
      </div>
    );
  }

  return (
    <div className={s.root}>
      <div className={s.tbar}>
        <AppIcon app="hub" size={20} />
        <b>HANA One</b>
        <span className={s.tUrl}>Ask HANA · one.hanalogisticsvn.com</span>
        <span className={s.grow} />
        <span className={s.tWin} aria-hidden="true"><span><IconMinus size={14} /></span><span><IconSquare size={12} /></span><span><IconX size={14} /></span></span>
      </div>
      <RailPortal>{railNode}</RailPortal>
      <div className={s.body}>
        <aside aria-labelledby="ah-side" className={s.side}>
          <div className={s.sideHead}>
            <b id="ah-side">{sideTitle}</b>
            <button type="button" className={s.plus} aria-label="Hỏi mới" title="Hỏi mới" onClick={newConv}><IconPlus size={16} stroke={2.2} aria-hidden="true" /></button>
          </div>
          {side}
        </aside>
        <main aria-label="Hội thoại Ask HANA" className={s.main}>
          <header className={s.mHead}>
            <h1>{conv === 'new' ? 'Hỏi mới' : CONV[conv][0]}</h1>
            <span className={s.seat} title="Gợi ý và dữ liệu theo ghế đang giữ">[SALE-MGR] DAVID</span>
            <span className={s.lockTip} title="Ask HANA không trả số tiền, không tự thực thi. Thao tác ghi mở sang HANA Ezy để anh duyệt."><IconLock size={14} stroke={2} aria-hidden="true" /></span>
          </header>
          <div className={s.scroll}>{stage}</div>
          {composer}
        </main>
      </div>
      {confirm}
      {overlays}
    </div>
  );
}
